import Vue from "vue";
import {checkSyntaxError} from "../../../../shared/js-parser";

import jsError from "../internal/js-error";
const jsErrorComponent = Vue.extend(jsError);

export const BUS = new Vue();

const entryByUuid = {
	/*
	<uuid>: {
		fragmentContent,
		board,
		feedback : {
			coordinates: {
				<name> : {userEditable, x, y, xName, yName, xValid, yValid}
			}
		}
	}
	*/
};

/**
 * Erstellt einen Punkt, dessen Koordinaten an MathCoach gesendet werden können.
 * Optional können die Koordinaten vom Benutzer über das Feedback-Formular editiert
 * werden. <br>
 * <b>WICHTIG</b>: Eine so erzeugte Koordinate verhält sich exakt wie ein herkömmlicher Punkt, lediglich
 * die Feedback-Funktionalität wurde hinzugefügt.
 **/
const pluginCoordinate = function (board, parents, attributes) {
	// Attribute validieren und aufbereiten
	const userEditable = (attributes.editable === true);
	if ((!attributes.name) || (attributes.name.trim().length === 0)) {
		throw new Error("a coordinate should have a valid name attribute!");
	}
	const name = attributes.name.trim();
	const uuid = board._UUID;
	const entry = entryByUuid[uuid];
	
	// Point erstellen
	const jsxPoint = board.create("point", parents, attributes);
	jsxPoint.on("drag", function () {
		entry.feedback.coordinates[name] = {
			// keep existing
			...entry.feedback.coordinates[name],
			// overwrite x and y
			x: jsxPoint.X().toFixed(3),
			y: jsxPoint.Y().toFixed(3),
			xValid: true,
			yValid: true
		};
		BUS.$emit("feedback-changed", { feedback: entry.feedback, targetUuid: uuid });
		// client validation
		doValidation(uuid);
	});
	//Change dependency after mouse released
	board.on("up", function() {
		entry.feedback.coordinates[name] = {
			// keep existing
			...entry.feedback.coordinates[name],
			// overwrite x and y
			x: jsxPoint.X().toFixed(3),
			y: jsxPoint.Y().toFixed(3),
			xValid: true,
			yValid: true
		};
		BUS.$emit("feedback-changed", { feedback: entry.feedback, targetUuid: uuid });
		// client validation
		doValidation(uuid);
	});
	// put coordinate into feedback
	entry.feedback.coordinates[name] = {
		x: jsxPoint.X().toFixed(3),
		y: jsxPoint.Y().toFixed(3),
		userEditable,
		// name of the parameters on submit, see backend
		xName: `gfx_coordinate_${uuid}_${name}_X`,
		yName: `gfx_coordinate_${uuid}_${name}_Y`,
		xValid: true,
		yValid: true
	};
	BUS.$emit("feedback-changed", { feedback: entry.feedback, targetUuid: uuid });
	// client validation
	doValidation(uuid);
	
	return jsxPoint;
};
try {
	JXG.registerElement('coordinate', pluginCoordinate); // eslint-disable-line no-undef
	JXG.Options.text.useMathJax = true; // eslint-disable-line no-undef
}catch (e) {
	console.error("Cannot load JXG");
	// Do nothing
}
const doValidation = function (uuid) {
	const feedback = entryByUuid[uuid].feedback;
	let result = true;
	Object.keys(feedback.coordinates).forEach(name => {
		const coordinate = feedback.coordinates[name];
		result = result && coordinate.xValid;
		result = result && coordinate.yValid;
	});
	BUS.$emit("validated", { targetUuid: uuid, allValid: result });
};

export const feedbackToForm = function (uuid) {
	console.log("feedback to json", uuid);
	const feedback = entryByUuid[uuid].feedback;
	const result = {};
	result[uuid] = uuid; // damit das board als submitted gilt
	Object.keys(feedback.coordinates).forEach(name => {
		const coordinate = feedback.coordinates[name];
		result[coordinate.xName] = coordinate.x;
		result[coordinate.yName] = coordinate.y;
	});
	return result;
};

export const applyFeedbackNewFeedback = function (uuid, feedback) {
	const board = entryByUuid[uuid].board;
	entryByUuid[uuid].feedback = feedback;
	Object.keys(feedback.coordinates).forEach(name => {
		const { x, y, userEditable } = feedback.coordinates[name];
		if (userEditable) {
			try {
				board.elementsByName[name].moveTo([
					eval(x.replace(",", ".")),
					eval(y.replace(",", "."))
				]);
			} catch (error) {
				// ignore
			}
		}
	});
	// client validation
	doValidation(uuid);
};

/**
 * fragmentContent = {
 *     "uuid": .... ,
 *     "gfxScript": "var A = ....",
 *     "width": 800,
 *     "height": 600,
 *     "attributes": {
 *         "axis": true,
 *         "grid": false,
 *         "xAxis": {
 *             ....
 *         },
 *         "yAxis:" {
 *             ....
 *         }
 *     }
 * }
 * @param {String} idOfDomTarget
 * @param {Object} fragmentContent
 * */
export const newBoard = function (idOfDomTarget, fragmentContent) {
	const {gfxScript, width, height } = fragmentContent;
	// set with and height
	const domTarget = window.document.getElementById(idOfDomTarget);
	domTarget.style.width = width + "px";
	domTarget.style.height = height + "px";
	// check error if any
	let syntaxError = checkSyntaxError( gfxScript );
	if (syntaxError === null) {
		constructBoard(idOfDomTarget, fragmentContent);
	}else {
		console.log("Syntax Error", syntaxError);
		showJSError(syntaxError, domTarget);
	}
};

function showJSError(error, domTarget) {
	let errorMessage = new jsErrorComponent({
		propsData: { fragment: error }
	});
	errorMessage.$slots['module'] = "Gfx";
	errorMessage.$mount();
	domTarget.appendChild(errorMessage.$el);
}

function constructBoard(idOfDomTarget, fragmentContent) {
	const { uuid, gfxScript } = fragmentContent;
	
	// construct the board setup
	const defaultBoardOption = {
		axis: true, boundingbox: [-8, 4, 8, -4]
	};
	let authorDefinedAttributes = fragmentContent.attributes;
	let effectiveBoardAttributes = Object.assign({},  defaultBoardOption,authorDefinedAttributes);
	
	const board = JXG // eslint-disable-line no-undef
		.JSXGraph.initBoard(idOfDomTarget, effectiveBoardAttributes);
	
	board._UUID = uuid;
	// register the board
	entryByUuid[uuid] = {
		board,
		fragmentContent,
		feedback: {
			coordinates: {}
		}
	};
	let isSetRepositionTicks = effectiveBoardAttributes["repositionTickLabels"] !== undefined;
	let repositionTickLabels = isSetRepositionTicks ? effectiveBoardAttributes["repositionTickLabels"] : true;
	let isUpdateBoardAfterRenderMathFormRequired =  false ;
	
	//constructBoardAxis(board, effectiveBoardAttributes);
	let xAxis = effectiveBoardAttributes["xAxis"];
	if (xAxis) {
		let tickLabelAttribute = {
			label: {
				anchorX: "middle",
				anchorY: "top",
				offset: [0,-3]
			}
		};
		isUpdateBoardAfterRenderMathFormRequired = drawAxis( board, xAxis, [[0,0],[1,0]], tickLabelAttribute ) || isUpdateBoardAfterRenderMathFormRequired;
	}
	
	let yAxis = effectiveBoardAttributes["yAxis"];
	if (yAxis) {
		let tickLabelAttribute = {
			tickEndings: [1, 0],
			label:{
				anchorX: "right",
				anchorY: "middle",
				offset: [-6, 0]
			}
		};
		isUpdateBoardAfterRenderMathFormRequired = drawAxis( board, yAxis, [[0,0],[0,1]], tickLabelAttribute ) || isUpdateBoardAfterRenderMathFormRequired;
	}
	if ( repositionTickLabels ) {
		try {
			if (isUpdateBoardAfterRenderMathFormRequired) {
				MathJax.Hub.Queue(() => board.update()); // eslint-disable-line no-undef
			}
		} catch (e) {
			// Do nothing
		}
	}
	// run the users script
	const runUsersScript = new Function(`return function(board){ ${gfxScript} }`).call();
	runUsersScript(board);
}

const DEFAULT_AXIS_COLOR = "#adadad";
const TICK_MAJOR_HEIGHT = 11;
const TICK_MINOR_HEIGHT = 7;

const DEFAULT_TICKS_ATTRIBUTES = {
	shadow: false,
	fixed: true,
	precision: 3,
	drawLabels: true,
	strokeColor: DEFAULT_AXIS_COLOR,
	highlightFillColor: DEFAULT_AXIS_COLOR,
	highlightStrokeColor: DEFAULT_AXIS_COLOR
};

const DEFAULT_AXIS_ATTRIBUTES = {
	/*straightLast:true, straightFirst:true,*/
	shadow:false,
	draggable:false,
	fixed:true,
	lastArrow:{
		type: 1,
		size: 8
	},
	ticks: {
		visible: 'false',
		needsRegularUpdate: false,
		drawLabels: false,
		drawZero: false,
		insertTicks: false,
		ticksDistance: 10000000,
	},
	
	trace:false
};


const GRID_ATTRIBUTES = {
	shadow: false,
	fixed: true,
	draggable:false,
	highlight: false,
	
	needsRegularUpdate: false,
	hasGrid: false,
	
	strokeColor: '#C0C0C0',
	fillColor: '#C0C0C0',
	
	strokeOpacity: 0.5,
	strokeWidth: 1,
	dash: 0,
	snapToGrid: false,
};

function drawAxis(board, axisAttributes, points, tickLabelAttribute) {
	// axis
	let color = axisAttributes["color"] || DEFAULT_AXIS_COLOR ;
	let label = axisAttributes["label"] || "";
	let attributes = Object.assign({}, DEFAULT_AXIS_ATTRIBUTES, {
		label: label,
		color: color, highlightFillColor: color, highlightStrokeColor: color
	});
	let line = board.create("axis", points, attributes);
	// ticks
	let majorTicks = axisAttributes["majorTicks"];
	let updateTickLabelsPosition = false;
	if (majorTicks) {
		updateTickLabelsPosition = drawTicks(board, line, majorTicks, color, TICK_MAJOR_HEIGHT, tickLabelAttribute) || updateTickLabelsPosition  ;
	}
	let minorTicks = axisAttributes["minorTicks"];
	if (minorTicks) {
		updateTickLabelsPosition = drawTicks(board, line, minorTicks, color, TICK_MINOR_HEIGHT, tickLabelAttribute) || updateTickLabelsPosition  ;
	}
	// grids
	let grid = axisAttributes["grid"];
	if (grid) {
		if (majorTicks) {
			if (points[1][1]) { // draw horizontal grids
				majorTicks.forEach((tick) => {
					let gridLine = [ [0, tick[0]], [1, tick[0]] ];
					board.create('line', gridLine, GRID_ATTRIBUTES );
				});
			} else if (points[1][0]) { // drawn vertical grids
				majorTicks.forEach((tick) => {
					let gridLine = [ [tick[0], 0], [tick[0], 1] ];
					board.create('line', gridLine, GRID_ATTRIBUTES );
				});
			}
		}
	}
	return updateTickLabelsPosition;
}

function drawTicks(board, axis, ticks, color, tickHeight, tickLabelAttribute) {
	let tickValues = [],
		tickLabels = [];
	ticks.forEach( t => {
		tickValues.push(t[0]);
		tickLabels.push(t[1].replace(/\\/g,"\\\\"));
	});
	let updateLabelPosition = tickLabels.find( label => label.includes("$") ) && true;
	let ticksAttribute = Object.assign({}, DEFAULT_TICKS_ATTRIBUTES, tickLabelAttribute, {
		labels: tickLabels,
		majorHeight: tickHeight,
		color:color, highlightStrokeColor: color, highlightFillColor:color
	});
	board.create("ticks", [axis, tickValues],  ticksAttribute);
	return updateLabelPosition ;
}
